import { QueryFunctionContext, useQueries } from '@tanstack/react-query';
import { getAllAnnotationAssignments, getAnnotationAssignments } from 'api/annotationAssignments';
import { every, isEmpty, map, reduce } from 'es-toolkit/compat';
import { CasesParams } from 'utils/useCasesParams/types';
import { useQueryWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';

export const useAllAnnotationAssignmentsQuery = ({
  enabled = true,
  isGlobal = false,
}: {
  enabled?: boolean;
  isGlobal?: boolean;
}) =>
  useQueryWithErrorAndRetrySnackbar(
    ['annotationAssignments', 'all'],
    ({ signal }: QueryFunctionContext) => getAllAnnotationAssignments({ isGlobal, signal }),
    {
      enabled,
      queriedEntityName: 'annotation assignments',
    }
  );

export const useAnnotationAssignmentByStain = ({
  stainTypes,
  casesParams,
  enabled = true,
}: {
  stainTypes: string[];
  casesParams: CasesParams;
  enabled?: boolean;
}) => {
  const annotationAssignmentsQueries = useQueries({
    queries: map(stainTypes, (stainingType) => {
      return {
        queryKey: ['annotationAssignments', { ...casesParams, slideStainType: stainingType }],
        queryFn: ({ signal }: QueryFunctionContext) =>
          getAnnotationAssignments({ ...casesParams, slideStainType: stainingType }, signal),
        enabled,
      };
    }),
  });

  const finishLoadingAnnotationAssignments =
    !isEmpty(annotationAssignmentsQueries) && every(annotationAssignmentsQueries, (query) => query.isFetched);

  const annotationAssignmentsByStain = reduce(
    annotationAssignmentsQueries,
    (acc, query, index) => {
      const stainType = stainTypes[index];
      return { ...acc, [stainType]: query.data?.annotationAssignments || [] };
    },
    {}
  );

  return { annotationAssignmentsByStain, finishLoadingAnnotationAssignments };
};
